import { Locale, formatRelative, setDefaultOptions } from 'date-fns';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';

import { AccountDeletionReason, FormFieldType, OrganisationType, VatSchedule } from '@/shared/types/enum';
import { Enum, enumKeys } from '@/shared/utils/enum';
import { invert } from '@/shared/utils/object';

import { Individual, User } from '@prisma/client';
import { enGB } from 'date-fns/locale';

export const DEFAULT_LOCALE = enGB as Locale & { code: 'en-GB' };

setDefaultOptions({ locale: DEFAULT_LOCALE });

export const DEFAULT_TIMEZONE = 'Europe/London';

export const dateFormat = 'dd/MM/yy';

export const dateInputFormat = 'dd/MM/yyyy';

export const monthFormat = 'MMM';

export const longMonthFormat = 'MMMM';

export const dayMonthFormat = 'd MMM';

export const dayLongMonthFormat = 'd MMMM';

export const dayMonthYearFormat = 'd MMM yy';

export const weekdayMonthYearFormat = 'EEE d MMM yy';

export const timeFormat = 'H:mm';

export const dateTimeFormat = `dd/MM/yy 'at' H:mm`;

export const dayMonthYearTimeFormat = `d MMM yy 'at' H:mm`;

export const exportDateFormat = 'dd/MM/yyyy';

export const exportDateTimeFormat = `dd/MM/yyyy H:mm:ss`;

const ENUM_KEY_RELACEMENTS: Record<string, string> = {
  Aml: 'AML',
  AmlCheck: 'AML RA',
  Cis: 'CIS',
  Mtd: 'MTD',
  It: 'IT',
  Vat: 'VAT',
  Boolean: 'Yes/No',
  CIC: 'CIC',
  CIO: 'CIO',
  SCIO: 'SCIO',
};

export const parseOrCloneDate = (date: Date | string | null | undefined) => {
  if (date === null || date === undefined) {
    return undefined;
  }

  const parsedDate = new Date(typeof date === 'string' ? Date.parse(date) : date.getTime());

  return isNaN(parsedDate.getTime()) ? undefined : parsedDate;
};
export const formatDate = (date: Date | string | null | undefined) => formatDateCustom(date, dateFormat);

export const formatDateTime = (date: Date | string | null | undefined) => formatDateCustom(date, dateTimeFormat);

export const formatMonth = (date: Date | string | null | undefined) => formatDateCustom(date, monthFormat);

export const formatLongMonth = (date: Date | string | null | undefined) => formatDateCustom(date, longMonthFormat);

export const formatDayMonth = (date: Date | string | null | undefined) => formatDateCustom(date, dayMonthFormat);

export const formatDayLongMonth = (date: Date | string | null | undefined) =>
  formatDateCustom(date, dayLongMonthFormat);

export const formatDayMonthYearTime = (date: Date | string | null | undefined) =>
  formatDateCustom(date, dayMonthYearTimeFormat);

export const formatDayMonthYear = (date: Date | string | null | undefined) =>
  formatDateCustom(date, dayMonthYearFormat);

export const formatWeekdayMonthYear = (date: Date | string | null | undefined) =>
  formatDateCustom(date, weekdayMonthYearFormat);

export const formatTime = (date: Date | string | null | undefined) => formatDateCustom(date, timeFormat);

export const formatDateRelative = (date: Date | string | null | undefined) => {
  const baseDate = utcToZonedTime(new Date(), DEFAULT_TIMEZONE);

  date = parseOrCloneDate(date);

  return date
    ? upperCaseFirst(formatRelative(utcToZonedTime(date, DEFAULT_TIMEZONE), baseDate))
        // Mild hack to shorten year to 2 digits if present
        .replace(/\/\d{4}$/, (year) => `/${year.slice(-2)}`)
    : '';
};

export const formatDateCustom = (date: Date | string | null | undefined, pattern: string) => {
  date = parseOrCloneDate(date);

  if (!date) {
    return '';
  }

  return date ? formatInTimeZone(date, DEFAULT_TIMEZONE, pattern) : '';
};

export const formatEnumKey = (value: string | null | undefined, separator = ' ') => {
  if (!value) return '';
  if (ENUM_KEY_RELACEMENTS[value]) return ENUM_KEY_RELACEMENTS[value];
  const parts = value.match(/[A-Z]+(?=[A-Z][a-z])|[A-Z]?[a-z]+|[0-9]+/g) ?? [];
  return parts.map((p) => ENUM_KEY_RELACEMENTS[p] ?? p).join(separator);
};

export const formatEnumKeys = (type: Enum) => enumKeys(type).map((key) => formatEnumKey(key));

export const formatEnum = (type: Enum, value: string | number | null | undefined) => {
  const inverted = invert(type);
  return value !== null && value !== undefined && inverted[value] !== undefined ? formatEnumKey(inverted[value]) : '';
};

export const formatOrganisationTypeDropdown = (value: OrganisationType | string | null | undefined) => {
  const dropdownLabels: Partial<Record<OrganisationType, string>> = {
    [OrganisationType.LimitedLiabilityPartnership]: 'Limited Liability Partnership (LLP)',
    [OrganisationType.LimitedPartnership]: 'Limited Partnership (LP)',
    [OrganisationType.CIC]: 'Community Interest Company (CIC)',
    [OrganisationType.CIO]: 'Charitable Incorporated Organisation (CIO)',
    [OrganisationType.SCIO]: 'Scottish Charitable Incorporated Organisation (SCIO)',
  };

  if (value === null || value === undefined) {
    return '';
  }

  return dropdownLabels[value as OrganisationType] ?? formatEnum(OrganisationType, value);
};

export const formatAccountDeletionMethod = (reason: string | null | undefined) => {
  if (!reason) return '';
  return reason === AccountDeletionReason.Inactivity ? 'Automatic' : 'Manual';
};

export const formatVatSchedule = (schedule: VatSchedule | null | undefined) =>
  formatEnum(VatSchedule, schedule).split(' ').join('/');

export const formatVatScheduleKeys = () => formatEnumKeys(VatSchedule).map((key) => key.split(' ').join('/'));

export const formatClassName = (text: string | null | undefined) =>
  text ? formatEnumKey(text).toLowerCase().replaceAll(' ', '-') : '';

export const formatOrdinal = (value: number | null | undefined) => {
  const ordinalSuffix = (value: number) => {
    if (value > 3 && value < 21) {
      return 'th';
    }

    switch (value % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  return value !== null && value !== undefined ? `${value}${ordinalSuffix(value)}` : '';
};

export const camelize = (text: string) =>
  text.replace(/^([A-Z])|[\s-_]+(\w)/g, (match, p1, p2) => (p2 ? p2.toUpperCase() : p1.toLowerCase()));

export const decamelize = (text: string, separator: string) =>
  text
    .replace(/([a-z\d])([A-Z])/g, `$1${separator}$2`)
    .replace(/([A-Z]+)([A-Z][a-z\d]+)/g, `$1${separator}$2`)
    .toLowerCase();

export const upperCaseFirst = (text: string) => text[0].toUpperCase() + (text.length > 1 ? text.slice(1) : '');

const HYPHENATED_ACRONYMS: Record<string, string> = {
  cic: 'CIC',
  eeig: 'EEIG',
  llp: 'LLP',
  plc: 'PLC',
  psc: 'PSC',
  se: 'SE',
  uk: 'UK',
};

const formatHyphenatedPart = (part: string, titleCase = true) =>
  HYPHENATED_ACRONYMS[part] ?? (titleCase ? upperCaseFirst(part) : part);

export const formatCompanyNumber = (companyNumber: string | null | undefined) => {
  companyNumber = companyNumber
    ?.trim()
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '');

  return companyNumber && companyNumber.length <= 8 ? companyNumber.padStart(8, '0') : '';
};

export const formatCompanyStatus = (companyStatus: string | null | undefined) => {
  if (!companyStatus) return '';

  const parts = companyStatus.split('-');
  const mappedParts = parts.map((part) => upperCaseFirst(part));
  return mappedParts.join(companyStatus === 'converted-closed' ? '/' : ' ');
};

export const formatOfficerRole = (officerRole: string | null | undefined) => {
  if (!officerRole) return '';

  return officerRole
    .split('-')
    .filter(Boolean)
    .map((part, index) => formatHyphenatedPart(part, index === 0))
    .join(' ');
};

export const formatCompanyStatusDetail = (companyStatusDetail: string | null | undefined) => {
  if (!companyStatusDetail) return '';

  return companyStatusDetail
    .split('-')
    .map((part, index) => formatHyphenatedPart(part, index === 0))
    .join(' ');
};

export const formatCompanyStatusDisplay = (companyStatus: string, companyStatusDetail: string | null | undefined) => {
  if (!companyStatusDetail) return formatCompanyStatus(companyStatus);
  return formatCompanyStatus(companyStatus) + ' - ' + formatCompanyStatusDetail(companyStatusDetail);
};

export const clip = (text: string | null | undefined, length: number) => {
  if (text) {
    if (text.length > length - 3) {
      return `${text.substring(0, length - 3)}...`;
    }

    return text;
  }

  return '';
};

export const formatList = (items: string[]) =>
  items.map((item, index) => `${index > 0 ? (index < items.length - 1 ? ', ' : ' and ') : ''}${item}`).join('');

export const formatUserName = (user: Partial<Pick<User, 'firstName' | 'lastName'>>) => {
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Unnamed User';
};

export const formatSubTaskCompletedTooltip = (date: Date | string, userName?: string | null) => {
  const when = formatDayMonthYearTime(date);

  return userName ? `Completed by ${userName} on ${when}` : `Completed on ${when}`;
};

export const formatIndividualName = (individual: Partial<Pick<Individual, 'givenName' | 'surname'>>) => {
  return [individual.givenName, individual.surname].filter(Boolean).join(' ') || 'Unnamed Individual';
};

export const formatIndividualNameSurnameFirst = (individual: Partial<Pick<Individual, 'givenName' | 'surname'>>) =>
  [individual.surname, individual.givenName].filter(Boolean).join(', ');

const numberFormatter = new Intl.NumberFormat(DEFAULT_LOCALE.code);

export const formatNumber = (value: string | number | null | undefined, decimalPoints?: number) => {
  if (value == null || value === '') {
    return '';
  }

  if (typeof value === 'string') {
    value = parseFloat(value);
  }

  if (isNaN(value)) {
    return '';
  }

  const formatter =
    decimalPoints !== undefined
      ? new Intl.NumberFormat(DEFAULT_LOCALE.code, {
          minimumFractionDigits: decimalPoints,
          maximumFractionDigits: decimalPoints,
        })
      : numberFormatter;

  return formatter.format(value);
};

export const formatCustomFieldValue = (
  type: FormFieldType | undefined,
  value: string | null | undefined,
  decimalPoints?: number,
) => {
  if (value == null || value === '') {
    return '';
  }

  if (type === FormFieldType.Date) {
    return formatDate(value);
  }

  if (type === FormFieldType.Boolean) {
    return value === 'true' ? 'Yes' : 'No';
  }

  if (type === FormFieldType.Number) {
    return formatNumber(value, decimalPoints);
  }

  return value;
};

export function camelToKebabCase(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
}

export function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
